import React, { useMemo } from 'react';

const TrendsDoubleDoubleReport = ({ data, teams, loading, oddsData, market = 'player_double_double', onPlayerClick, isPastYear = false, sport = 'nba', currentTeamByPlayerId: externalRosterMap = null }) => {
  // Parse teams from "MIA @ WSH" format
  const parseTeams = () => {
    if (!teams) return { away: null, home: null };
    const parts = teams.split('@').map(t => t.trim());
    return { away: parts[0] || null, home: parts[1] || null };
  };

  const { away: awayTeam, home: homeTeam } = parseTeams();

  // Prefer the externally-supplied roster (from /today payload — complete current roster).
  // Fall back to deriving from odds data, which is market-scoped and may miss players.
  const oddsRosterMap = useMemo(() => {
    const map = new Map();
    if (!oddsData?.players) return map;
    oddsData.players.forEach(p => {
      const pid = String(p.playerId);
      const team = p.team || p.teamId;
      if (pid && team && !map.has(pid)) {
        map.set(pid, team);
      }
    });
    return map;
  }, [oddsData]);
  const currentTeamByPlayerId = (externalRosterMap && externalRosterMap.size > 0) ? externalRosterMap : oddsRosterMap;
  const isTripleDouble = market === 'player_triple_double';
  const title = isTripleDouble ? 'TRIPLE DOUBLES' : 'DOUBLE DOUBLES';

  // Extract players with odds from odds data
  const playersWithOdds = useMemo(() => {
    if (!oddsData?.players) return new Set();

    const playerIds = new Set();
    oddsData.players.forEach(player => {
      if (player.isAvailable === false) return;
      if (player.playerId) {
        playerIds.add(String(player.playerId));
      }
    });
    return playerIds;
  }, [oddsData]);

  const playerHasOdds = (playerId) => {
    return playersWithOdds.has(String(playerId));
  };

  // Get stats for a player
  const getStats = (player) => {
    const statData = player[market];
    if (!statData) return null;

    const seasonMilestone = statData.season?.milestones?.['1'];
    const last5Milestone = statData.last5?.milestones?.['1'];
    const last10Milestone = statData.last10?.milestones?.['1'];

    return {
      seasonCount: seasonMilestone?.hit || 0,
      seasonGames: statData.season?.gamesPlayed || 0,
      last5Count: last5Milestone?.hit || 0,
      last10Count: last10Milestone?.hit || 0
    };
  };

  const styles = {
    container: {
      width: '100%',
      color: '#fff',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      textAlign: 'center',
      marginBottom: '0.5rem'
    },
    title: {
      fontSize: '1.2em',
      fontWeight: '600',
      fontFamily: "'Oswald', sans-serif",
      margin: 0,
      textTransform: 'uppercase',
      color: '#ffc107'
    },
    columnsContainer: {
      display: 'flex',
      gap: '0.5rem',
      flex: 1,
      overflow: 'hidden'
    },
    teamColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    teamHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.4rem',
      background: '#222',
      borderRadius: '0.25rem',
      marginBottom: '0.5rem'
    },
    teamLogo: {
      width: '24px',
      height: '24px',
      objectFit: 'contain'
    },
    teamName: {
      fontSize: '0.85rem',
      fontWeight: 'bold'
    },
    playersList: {
      flex: 1,
      overflowY: 'auto'
    },
    playerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.35rem',
      borderBottom: '1px solid #333',
      background: '#1a1a1a',
      borderRadius: '0.35rem',
      marginBottom: '0.35rem'
    },
    rank: {
      width: '18px',
      fontSize: '0.75rem',
      color: '#888',
      textAlign: 'center',
      flexShrink: 0
    },
    headshot: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      objectFit: 'cover',
      flexShrink: 0
    },
    playerInfo: {
      flex: 1,
      minWidth: 0
    },
    playerName: {
      fontSize: '0.75rem',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    statsLine: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '0.1rem'
    },
    seasonCount: {
      fontSize: '0.85rem',
      fontWeight: 'bold',
      color: '#4ade80'
    },
    recentStats: {
      fontSize: '0.65rem',
      color: '#94a3b8'
    },
    loading: {
      textAlign: 'center',
      padding: '2rem',
      color: '#888'
    },
    noData: {
      textAlign: 'center',
      padding: '0.5rem',
      color: '#666',
      fontSize: '0.75rem'
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading data...</div>;
  }

  const playersData = data?.players || [];

  const renderPlayerRow = (player, index, stats) => {
    const headshotUrl = player.playerId
      ? `https://a.espncdn.com/combiner/i?img=/i/headshots/${sport}/players/full/${player.playerId}.png&w=96&h=70&cb=1`
      : null;

    const hasOdds = playerHasOdds(player.playerId);
    const rowStyle = {
      ...styles.playerRow,
      ...(hasOdds ? {} : { opacity: 0.25 })
    };

    return (
      <div key={player.playerId || index} style={rowStyle}>
        <div style={styles.rank}>{index + 1}</div>
        {headshotUrl && (
          <img
            style={styles.headshot}
            src={headshotUrl}
            alt={player.playerName}
            onError={(e) => e.target.style.display = 'none'}
          />
        )}
        <div style={styles.playerInfo}>
          <div style={{ ...styles.playerName, ...(onPlayerClick ? { cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#6b7280' } : {}) }} onClick={onPlayerClick ? () => onPlayerClick(player.playerId, player.playerName, player.team) : undefined}>
            {player.playerName}
            {isPastYear && player.team && currentTeamByPlayerId.get(String(player.playerId)) && player.team !== currentTeamByPlayerId.get(String(player.playerId)) && (
              <span style={{ color: '#9ca3af', fontWeight: 'normal', marginLeft: '4px' }}>({player.team})</span>
            )}
          </div>
          <div style={styles.statsLine}>
            <span style={styles.seasonCount}>{stats.seasonCount}</span>
            <span style={styles.recentStats}>
              L5: {stats.last5Count} | L10: {stats.last10Count}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderTeamColumn = (teamAbbrev) => {
    if (!teamAbbrev) {
      return <div style={styles.noData}>No team data</div>;
    }

    const teamPlayers = isPastYear
      ? playersData.filter(p => currentTeamByPlayerId.get(String(p.playerId)) === teamAbbrev)
      : playersData.filter(p => p.team === teamAbbrev);

    if (teamPlayers.length === 0) {
      return <div style={styles.noData}>No players for {teamAbbrev}</div>;
    }

    // Get stats for each player and filter/sort
    const playersWithStats = teamPlayers
      .map(player => ({
        player,
        stats: getStats(player)
      }))
      .filter(({ stats }) => stats && stats.seasonCount > 0)
      .sort((a, b) => b.stats.seasonCount - a.stats.seasonCount);

    if (playersWithStats.length === 0) {
      return <div style={styles.noData}>No data available</div>;
    }

    return playersWithStats.slice(0, 10).map(({ player, stats }, index) =>
      renderPlayerRow(player, index, stats)
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>{title}</h2>
      </div>

      <div style={styles.columnsContainer}>
        {/* Away Team Column */}
        <div style={styles.teamColumn}>
          <div style={styles.teamHeader}>
            <img
              style={styles.teamLogo}
              src={`https://a.espncdn.com/i/teamlogos/${sport}/500-dark/${awayTeam?.toLowerCase()}.png`}
              alt={awayTeam}
              onError={(e) => e.target.style.visibility = 'hidden'}
            />
            <span style={styles.teamName}>{awayTeam}</span>
          </div>
          <div style={styles.playersList}>
            {renderTeamColumn(awayTeam)}
          </div>
        </div>

        {/* Home Team Column */}
        <div style={styles.teamColumn}>
          <div style={styles.teamHeader}>
            <img
              style={styles.teamLogo}
              src={`https://a.espncdn.com/i/teamlogos/${sport}/500-dark/${homeTeam?.toLowerCase()}.png`}
              alt={homeTeam}
              onError={(e) => e.target.style.visibility = 'hidden'}
            />
            <span style={styles.teamName}>{homeTeam}</span>
          </div>
          <div style={styles.playersList}>
            {renderTeamColumn(homeTeam)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrendsDoubleDoubleReport;
